import { Head } from '@inertiajs/react'
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'

import LinedTextArea from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import { noopToolSwap, type ToolAbout, ToolIoChip } from '../../components/tools/toolChrome'
import {
  COLOR_TOOL_CODECS,
  cssRgbaFor,
  getCodec,
  hexForColorPicker,
  MAX_CODEC_INPUT_CHARS,
} from '../../lib/codecs'

const COLOR_CODEC_ID = COLOR_TOOL_CODECS[0].id

/** Waits this long after the last keystroke before converting, so typing a color name doesn't
 * flash "invalid" errors for every partial substring. The picker bypasses this entirely. */
const AUTO_RUN_DEBOUNCE_MS = 350

/** --color-primary-container from application.css — the site's own accent orange. */
const DEFAULT_COLOR = '#ff6b00'

const COLOR_ABOUT: ToolAbout = {
  description:
    'Convert any CSS color to hex, rgb, hsl, and oklch. Conversion runs automatically as you type or as you pick a color. Input can be hex, rgb()/rgba(), hsl()/hsla(), hwb(), oklch(), or a named color such as cornflowerblue. The browser’s own CSS parser reads the value, so any color the browser accepts works here. Nothing is uploaded. The work stays in your browser.',
  libraries: [
    {
      name: 'MDN: CSS <color> value',
      href: 'https://developer.mozilla.org/en-US/docs/Web/CSS/color_value',
    },
    {
      name: 'Björn Ottosson — OKLab color space',
      href: 'https://bottosson.github.io/posts/oklab/',
    },
  ],
}

const SWATCH_CHECKERBOARD_STYLE = {
  backgroundImage: 'repeating-conic-gradient(#80808026 0% 25%, transparent 0% 50%)',
  backgroundSize: '16px 16px',
} as const

/** <input type="color"> needs a valid #rrggbb at all times; falls back to black while input is unparseable. */
function resolvePickerHex(input: string): string {
  try {
    return input.trim() ? hexForColorPicker(input) : '#000000'
  } catch {
    return '#000000'
  }
}

function ColorSwatch({ input }: { input: string }) {
  let rgba: string | null = null
  try {
    if (input.trim()) rgba = cssRgbaFor(input)
  } catch {
    rgba = null
  }

  return (
    <div
      className="h-20 w-full shrink-0 overflow-hidden rounded-md border border-outline-variant/20"
      style={SWATCH_CHECKERBOARD_STYLE}
      role="img"
      aria-label={rgba ? `Color preview: ${rgba}` : 'Color preview unavailable'}
    >
      <div className="h-full w-full transition-colors" style={{ backgroundColor: rgba ?? 'transparent' }} />
    </div>
  )
}

export default function ColorTool() {
  const codec = useMemo(() => getCodec(COLOR_CODEC_ID)!, [])
  const [mode, setMode] = useState<'encode' | 'decode'>('encode')
  // Prefilled with the site's own accent color (--color-primary-container). This makes the picker
  // swatch bright and on-brand on load, instead of an easy-to-miss black square.
  const [input, setInput] = useState(DEFAULT_COLOR)
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Accepts an override so the picker can run against the value it just picked. It does not wait
  // for a state update to land first. Calling setInput and run together in one handler would
  // otherwise use the previous `input` value, because this render's closure captured it before the
  // update landed.
  const run = useCallback(
    async (overrideInput?: string) => {
      const value = overrideInput ?? input
      try {
        setError(null)
        if (value.length > MAX_CODEC_INPUT_CHARS) {
          setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
          return
        }
        setBusy(true)
        const out = await Promise.resolve(codec.encode(value))
        setOutput(out)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not parse color')
      } finally {
        setBusy(false)
      }
    },
    [input, codec],
  )

  // Converts automatically as the text input changes, debounced. This is the tool's only trigger
  // now that the manual execute button is hidden. The picker calls run() directly (see below) for
  // instant feedback instead of waiting on this timer.
  useEffect(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      return
    }
    const timer = setTimeout(() => {
      void run()
    }, AUTO_RUN_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [input, run])

  const onPickerChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const hex = e.target.value
      setInput(hex)
      void run(hex)
    },
    [run],
  )

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const inputColumn = (
    <LinedTextArea
      fillHeight
      value={input}
      onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
      maxLength={MAX_CODEC_INPUT_CHARS}
      rows={14}
      placeholder={codec.placeholderEncode}
      aria-label="Color input"
    />
  )

  const outputSlot = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      <ColorSwatch input={input} />
      <LinedTextArea
        readOnly
        fillHeight
        value={output}
        rows={10}
        placeholder="Output appears here…"
        aria-label="Color output"
      />
    </div>
  )

  return (
    <>
      <Head title="Color converter" />
      <ToolWorkspace
        heading="Color converter"
        inputHeaderStart={<ToolIoChip>INPUT (CSS COLOR)</ToolIoChip>}
        inputHeaderEnd={
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-on-surface-variant uppercase">{codec.hintEncode}</span>
            <div className="flex items-center gap-1.5" title="Pick a color">
              <span className="material-symbols-outlined text-base text-on-surface-variant" aria-hidden>
                colorize
              </span>
              <input
                type="color"
                value={resolvePickerHex(input)}
                onChange={onPickerChange}
                aria-label="Pick a color"
                className="h-7 w-9 shrink-0 cursor-pointer rounded border border-outline-variant/40 bg-transparent p-0.5"
              />
            </div>
          </div>
        }
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly
        swapDisabled
        swapTitle="Not available"
        onSwap={noopToolSwap}
        onExecute={run}
        executeBusy={busy}
        hideExecute
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={String(input.length).padStart(6, '0')}
        statsAlgorithm={codec.statsLabel}
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel="CONVERT"
        outputSlot={outputSlot}
        about={COLOR_ABOUT}
      />
    </>
  )
}
